<template>
    <v-card class="fa-card rounded-lg" :class="{ 'fa-card--mobile': $vuetify.breakpoint.xs }">
        <v-btn icon small class="fa-card__close" title="Κλείσιμο" @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
        </v-btn>

        <div class="fa-card__photo">
            <v-img
                :src="agent.image ? `${assetsUrl}/${agent.image}` : defaultPlayerImg"
                contain
                class="fa-card__photo-img"
                @error="agent.image = null"
            />
        </div>

        <div class="fa-card__info">
            <div class="fa-card__name">{{ agent.surname }} {{ agent.name }}</div>
            <div class="fa-card__facts">
                <span v-if="agent.position" class="fa-chip fa-chip--dark">{{ positionLabel(agent.position) }}</span>
                <span v-if="agent.ageYears !== null" class="fa-fact">
                    <v-icon x-small>mdi-cake-variant-outline</v-icon>
                    {{ agent.ageYears }} ετών · {{ agent.birthday }}
                </span>
                <span v-if="agent.heightCm" class="fa-fact">
                    <v-icon x-small>mdi-human-male-height</v-icon>
                    {{ agent.heightCm }} cm
                </span>
            </div>

            <div class="fa-card__teams">
                <template v-if="agent.previousTeams.length">
                    <info-tip
                        v-for="team in agent.previousTeams"
                        :key="team.id"
                        :text="`${team.name}: ${team.games} ${team.games === 1 ? 'αγώνας' : 'αγώνες'}`"
                    >
                        <span class="fa-team">
                            <span class="fa-team__logo">
                                <v-img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" contain class="fa-team__logo-img" />
                                <v-icon v-else x-small color="orange">mdi-basketball</v-icon>
                            </span>
                            {{ team.name }}
                        </span>
                    </info-tip>
                </template>
                <span v-else class="fa-muted">Χωρίς προηγούμενη ομάδα</span>
            </div>

            <div v-if="agent.games" class="fa-stats">
                <div v-for="stat in statColumns" :key="stat.key" class="fa-stat" :class="{ 'fa-stat--highlight': stat.key === 'pir' }">
                    <span class="fa-stat__value">{{ formatStat(agent[stat.key], stat.percent) }}</span>
                    <span class="fa-stat__label">{{ stat.label }}</span>
                </div>
            </div>
            <div v-else class="fa-muted fa-card__no-stats">Δεν έχει αγώνες με στατιστικά ακόμα</div>

            <div class="fa-card__footer">
                <span v-if="agent.lastMatchDatetime" class="fa-card__last">
                    Τελευταίος αγώνας: {{ formatDate(agent.lastMatchDatetime) }}
                </span>
                <v-spacer />
                <v-btn small depressed class="fa-card__profile white--text" :to="{ name: 'Player', params: { id: agent.id } }">
                    Σελίδα παίκτη
                    <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
            </div>
        </div>
    </v-card>
</template>

<script>
    import { formatDate, formatStat, positionLabel } from '@/utils/freeAgents'

    // The card of a free agent: photo, facts, previous teams and the averages of all the matches
    export default {
        name: 'FreeAgentCard',
        props: {
            // a free agent as normalized by the free agents page (ageYears, heightCm, previousTeams)
            agent: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                statColumns: [
                    { key: 'games', label: 'ΑΓ' },
                    { key: 'minutes', label: 'ΛΕΠ' },
                    { key: 'points', label: 'ΠΟΝ' },
                    { key: 'rebounds', label: 'ΡΙΜ' },
                    { key: 'assists', label: 'ΑΣ' },
                    { key: 'steals', label: 'ΚΛ' },
                    { key: 'blocks', label: 'ΚΟΨ' },
                    { key: 'pir', label: 'PIR' },
                    { key: 'fieldGoalPercentage', label: 'FG%', percent: true },
                    { key: 'threePointPercentage', label: '3P%', percent: true },
                    { key: 'freeThrowPercentage', label: 'FT%', percent: true }
                ]
            }
        },
        methods: { formatDate, formatStat, positionLabel }
    }
</script>

<style scoped>
.fa-card {
    position: relative;
    display: flex;
    gap: 16px;
    padding: 16px;
    border-left: 4px solid #ff7133;
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

.fa-card__close {
    position: absolute;
    top: 6px;
    right: 6px;
}

.fa-card__photo {
    flex: 0 0 120px;
    width: 120px;
    height: 150px;
    padding: 4px;
    border-radius: 10px;
    background: linear-gradient(180deg, #f1f1f3, #e4e4e8);
    overflow: hidden;
}

.fa-card__photo-img {
    width: 100%;
    height: 100%;
}

.fa-card__info {
    flex: 1 1 auto;
    min-width: 0;
}

.fa-card__name {
    padding-right: 28px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.2;
}

.fa-card__facts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    margin-top: 6px;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
}

.fa-fact {
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.fa-chip {
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 700;
}

.fa-chip--dark {
    background: #0d0d0f;
    color: #fff;
}

.fa-card__teams {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
}

.fa-team {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px 2px 3px;
    border-radius: 12px;
    background: #f1f1f3;
    font-size: 0.75rem;
    font-weight: 700;
}

.fa-team__logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    overflow: hidden;
}

.fa-team__logo-img {
    width: 100%;
    height: 100%;
}

.fa-stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
    gap: 4px;
    margin-top: 12px;
}

.fa-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px 2px;
    border-radius: 6px;
    background: #f7f7f8;
}

.fa-stat__value {
    font-size: 0.95rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.fa-stat__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.05em;
}

.fa-stat--highlight {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
}

.fa-stat--highlight .fa-stat__value,
.fa-stat--highlight .fa-stat__label {
    color: #fff;
}

.fa-card__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}

.fa-card__last {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
}

.fa-card__profile {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

.fa-card__no-stats {
    margin-top: 12px;
}

.fa-muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.8rem;
    font-style: italic;
}

@media (max-width: 600px) {
    .fa-card--mobile {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .fa-card--mobile .fa-card__photo {
        flex-basis: auto;
        width: 140px;
        height: 170px;
    }

    .fa-card--mobile .fa-card__name {
        padding: 0 28px;
    }

    .fa-card--mobile .fa-card__facts,
    .fa-card--mobile .fa-card__teams,
    .fa-card--mobile .fa-card__footer {
        justify-content: center;
    }

    .fa-card--mobile .fa-card__info {
        width: 100%;
    }
}
</style>
